/**
 * Signatur unter dem Brief wie bei den E-Mails (Kundenfeedback Kanzlei Polz
 * 25.10.2026 Teil 3, VII: „Können wir beim Brief nicht standardmäßig
 * dieselbe Auswahl für die Signatur abspeichern wie bei den E-Mails?“).
 *
 * Quelle „email“: die E-Mail-Signatur des Verfassers – aufgelöst wie im
 * E-Mail-Fenster (services/emailSignature) und hier in Brieftext (ohne HTML,
 * ohne Foto) umgewandelt. Quelle „letter“: die Grußformel des Briefkopfs.
 * Welche Quelle vorbelegt ist, legt der Briefkopf je Unternehmen fest.
 */

import { gtKey } from '@/i18n/gt'

export type LetterSignatureSource = 'email' | 'letter'

export const LETTER_SIGNATURE_SOURCES: ReadonlyArray<{ value: LetterSignatureSource, label: string, hint: string }> = [
  {
    value: 'email',
    label: gtKey('Wie bei E-Mails'),
    hint: gtKey('Ihre E-Mail-Signatur – mit Namen, Kontaktdaten und Standort, so wie sie unter Ihren E-Mails steht.'),
  },
  {
    value: 'letter',
    label: gtKey('Grußformel des Briefkopfs'),
    hint: gtKey('Die im Briefkopf hinterlegte Grußformel mit Ihrem Namen.'),
  },
]

/** Gültige Quelle oder der Standard („wie bei E-Mails“). */
export function normalizeSignatureSource (value: unknown): LetterSignatureSource {
  return value === 'letter' ? 'letter' : 'email'
}

const ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '\'',
  middot: '·',
  ndash: '–',
  mdash: '—',
  bull: '•',
  auml: 'ä',
  ouml: 'ö',
  uuml: 'ü',
  Auml: 'Ä',
  Ouml: 'Ö',
  Uuml: 'Ü',
  szlig: 'ß',
  euro: '€',
}

function decodeEntities (text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === '#') {
      const n = code[1] === 'x' || code[1] === 'X' ? Number.parseInt(code.slice(2), 16) : Number.parseInt(code.slice(1), 10)
      return Number.isFinite(n) && n > 0 ? String.fromCodePoint(n) : match
    }
    return ENTITIES[code] ?? match
  })
}

/** Blockelemente, deren Ende eine neue Zeile beginnt. */
const BLOCK_END_RE = /<\/(div|p|li|tr|h[1-6]|table|ul|ol)\s*>/gi

/**
 * E-Mail-Signatur (HTML) → Brieftext: Zeilenumbrüche aus <br> und
 * Blockelementen, Bilder (Foto, Logo) und Markup entfallen, Leerraum je
 * Zeile wird gestrafft, höchstens zwei Leerzeilen am Stück bleiben (Platz
 * für die Unterschrift).
 */
export function signatureHtmlToText (html: string | null | undefined): string {
  const source = String(html || '')
  if (!source.trim()) {
    return ''
  }
  const withBreaks = source
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
    // Ein <br> direkt vor dem Ende eines Blocks erzeugt im Editor keine
    // eigene Zeile (<div><br></div> ist genau eine Leerzeile).
    .replace(/<br\s*\/?>\s*(?=<\/(div|p|li)\s*>)/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(BLOCK_END_RE, '\n')
    .replace(/<\/td\s*>/gi, ' ')
  const text = decodeEntities(withBreaks.replace(/<[^>]*>/g, ''))
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => line.replace(/\s+/g, ' ').trim())
  const out: string[] = []
  let blank = 0
  for (const line of lines) {
    if (!line) {
      blank += 1
      if (blank <= 2 && out.length > 0) {
        out.push('')
      }
      continue
    }
    blank = 0
    out.push(line)
  }
  return out.join('\n').trim()
}

/**
 * Vorbelegte Signatur eines Briefs: bei Quelle „email“ die E-Mail-Signatur
 * als Text – fehlt sie (keine E-Mail-Signatur hinterlegt), gilt wie bisher
 * die Grußformel des Briefkopfs.
 */
export function letterSignatureFor (
  source: LetterSignatureSource,
  emailSignatureText: string | null | undefined,
  letterSignature: string | null | undefined,
): string {
  const fromEmail = (emailSignatureText || '').trim()
  if (source === 'email' && fromEmail) {
    return fromEmail
  }
  return (letterSignature || '').trim()
}
